import 'swiper/css'
import 'swiper/css/navigation'

import { useMemo } from 'react'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { NavigationOptions, SwiperOptions } from 'swiper/types'

import { themeProps } from '@/cmsComponents/AllMachinesDetailed'
import Icon from '@/components/Icon'
import { useTonMachines } from '@/contexts/machines'
import { useTonPlans } from '@/contexts/plans'
import { IPromotionalTag } from '@/types/cms'
import cn from '@/utils/cn'
import useViewport from '@/utils/useViewport'

import TonMachineCard from '../MachineCardV2'
import CardMachineLarge from './components/CardMachineLarge'
import { NewTapTonCard } from './components/NewTapTonCard'

interface MachinesProps {
  title: string
  description?: string
  promotionalTag: IPromotionalTag
  themeLayout: themeProps
}

const navigationOptions: NavigationOptions = {
  nextEl: '#swiper-next-mv2',
  prevEl: '#swiper-prev-mv2',
  disabledClass: 'text-display-400'
}

const swiperOptions: SwiperOptions = {
  modules: [Navigation],
  slidesPerView: 'auto',
  spaceBetween: 16,
  slidesOffsetBefore: 16,
  slidesOffsetAfter: 16,
  navigation: navigationOptions
}

const Machines = ({
  title,
  description,
  promotionalTag,
  themeLayout
}: MachinesProps) => {
  const { currentPlan } = useTonPlans()
  const { machinesByCatalog, getMachinesSortedByASC } = useTonMachines()
  const { device } = useViewport()

  const machines = machinesByCatalog.get(currentPlan?.catalog || '')

  const sortedMachines = useMemo(() => {
    if (!machines) return []

    return getMachinesSortedByASC(machines)
  }, [getMachinesSortedByASC, machines])

  if (sortedMachines.length === 0) return null

  return (
    <>
      <div className="pt-40 w-full">
        <div id="maquininhas" className="anchor" />
        <div className="flex flex-col justify-center items-center w-full">
          <div className="flex flex-col gap-8 justify-center items-center max-w-screen-xl">
            <h2
              className="px-16 md:px-0 font-heading text-4xl lg:text-6xl font-bold text-center"
              dangerouslySetInnerHTML={{ __html: title }}
            />

            {description && (
              <p
                className="px-16 text-center ton-paragraph-16"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>

          {(themeLayout === 'default' ||
            (themeLayout === 'no-carousel-web-two-cards' &&
              device !== 'desktop')) && (
            <div
              className={cn(
                'pt-24 lg:pt-40 w-full',
                sortedMachines.length <= 3 && 'flex md:justify-center m-auto'
              )}
            >
              <Swiper
                {...swiperOptions}
                className={cn(
                  '!pb-40 max-w-[1300px] md:!overflow-visible',
                  sortedMachines.length === 2 && 'md:justify-center m-auto'
                )}
              >
                {sortedMachines?.map((machine) => (
                  <SwiperSlide
                    key={machine.name}
                    className="pt-[60px] sm:pt-[50px] max-w-[263px] md:max-w-[308px] !h-auto"
                  >
                    <TonMachineCard
                      className="!h-full"
                      {...machine}
                      promotionalTag={promotionalTag}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {themeLayout === 'no-carousel-web-two-cards' &&
            device === 'desktop' && (
              <div className="flex gap-16 xl:gap-40 px-24 xl:px-0 pt-16 pb-80 max-w-screen-xl">
                {sortedMachines?.map((machine) => (
                  <CardMachineLarge
                    key={machine.name}
                    {...machine}
                    promotionalTag={promotionalTag}
                  />
                ))}
              </div>
            )}

          {themeLayout === 'all-products' && (
            <div
              className={cn(
                'pt-24 lg:pt-40 w-full',
                sortedMachines.length <= 3 && 'flex md:justify-center m-auto'
              )}
            >
              <Swiper
                {...swiperOptions}
                className={cn(
                  '!pb-40 max-w-[1300px] md:!overflow-visible',
                  sortedMachines.length === 2 && 'md:justify-center m-auto'
                )}
              >
                {sortedMachines?.map((machine) => (
                  <SwiperSlide
                    key={machine.name}
                    className="pt-[60px] sm:pt-[50px] max-w-[263px] md:max-w-[308px] !h-auto"
                  >
                    <TonMachineCard
                      className="!h-full"
                      {...machine}
                      promotionalTag={promotionalTag}
                    />
                  </SwiperSlide>
                ))}

                <SwiperSlide className="pt-[60px] sm:pt-[50px] max-w-[263px] md:max-w-[308px] !h-auto">
                  <NewTapTonCard />
                </SwiperSlide>
              </Swiper>
            </div>
          )}
        </div>
      </div>
      <div
        id="arrow-container"
        className={cn(
          'flex gap-16 justify-center pb-40',
          sortedMachines.length < 3 && 'md:hidden'
        )}
      >
        <span
          id="swiper-prev-mv2"
          className="w-40 h-40 text-newTon-700 cursor-pointer"
        >
          <Icon
            name="arrow-right-2x"
            className="w-40 h-40 rotate-180 fill-current"
          />
        </span>
        <span
          id="swiper-next-mv2"
          className="w-40 h-40 text-newTon-700 cursor-pointer"
        >
          <Icon name="arrow-right-2x" className="w-40 h-40 fill-current" />
        </span>
      </div>
    </>
  )
}

export default Machines
